import { useMemo, useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { ShareButtons } from "@/components/social/ShareButtons";
import { COLOR_LIST } from "@/data/colors";
import { findNearestLibraryColor, mixHex, isValidHex, normalizeHex } from "@/lib/color-utils";
import { computeSynergy, readSynergyPrimaryFromLocation, buildSynergyShareUrl } from "@/lib/compatibility";
import { classifyAuraFamily, AURA_READS } from "@/lib/aura-extract";

// A curated, visually varied subset of the library used as a "pick your
// color" grid — using all 175 would overwhelm the picker UI.
const PICKER_COLORS = [
  "red", "coral", "tangerine", "gold", "yellow", "olive", "green", "emerald",
  "mint", "teal", "sky-blue", "blue", "navy", "indigo", "violet", "purple",
  "magenta", "pink", "rose", "charcoal", "gray", "beige", "cream", "black",
]
  .map((id) => COLOR_LIST.find((c) => c.id === id))
  .filter((c): c is NonNullable<typeof c> => Boolean(c));

function ColorPickerGrid({ onPick }: { onPick: (hex: string) => void }) {
  return (
    <div className="grid grid-cols-6 sm:grid-cols-8 gap-2 max-w-lg mx-auto">
      {PICKER_COLORS.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onPick(c.hex)}
          title={c.name}
          aria-label={`Choose ${c.name}`}
          className="aspect-square rounded-full ring-1 ring-border hover:ring-2 hover:ring-primary transition-all"
          style={{ backgroundColor: c.hex }}
        />
      ))}
    </div>
  );
}

export default function Synergy() {
  const senderHex = useMemo(() => readSynergyPrimaryFromLocation(), []);
  const [friendHex, setFriendHex] = useState<string | null>(null);
  const [hexInput, setHexInput] = useState("");
  const [hexError, setHexError] = useState("");
  const [ownHex, setOwnHex] = useState<string | null>(null);

  const hasMatrix = Boolean(senderHex && friendHex);

  useSeo({
    title: hasMatrix
      ? "Your Color Synergy Compatibility Matrix | Mychromamind"
      : "Color Synergy — Compare Color Compatibility | Mychromamind",
    description: hasMatrix
      ? "See how your two dominant colors blend, with a shared relationship profile grounded in color psychology."
      : "Generate a shareable Color Synergy link from your quiz result, or compare two colors to see a compatibility matrix and relationship profile.",
    path: "/synergy",
    // Every sender's link produces a different, low-uniqueness result page —
    // index only the canonical, param-free landing state to avoid an
    // unbounded set of thin near-duplicate pages in search results.
    noindex: Boolean(senderHex),
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Color Synergy Compatibility Matrix",
          applicationCategory: "LifestyleApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Compare two dominant colors from Mychromamind's personality quiz and see a compatibility score, blended color, and shared relationship profile.",
          url: "https://mychromamind.com/synergy",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mychromamind", item: "https://mychromamind.com/" },
            { "@type": "ListItem", position: 2, name: "Color Synergy", item: "https://mychromamind.com/synergy" },
          ],
        },
      ],
    },
  });

  const senderColor = senderHex ? findNearestLibraryColor(senderHex).color : null;
  const friendColor = friendHex ? findNearestLibraryColor(friendHex).color : null;

  const synergy =
    senderColor && friendColor
      ? computeSynergy([senderColor], [friendColor], senderColor.name, friendColor.name)
      : null;

  const blendHex = senderHex && friendHex ? mixHex(senderHex, friendHex) : null;

  const relationshipText =
    synergy && senderColor && friendColor
      ? `${senderColor.name} carries ${senderColor.keywords[0].toLowerCase()} and ${senderColor.keywords[1].toLowerCase()}, while ${friendColor.name} leans into ${friendColor.keywords[0].toLowerCase()} and ${friendColor.keywords[1].toLowerCase()}. ${synergy.description} Together, this pairing tends to show up as a dynamic between ${senderColor.keywords[2].toLowerCase()} and ${friendColor.keywords[2].toLowerCase()} — the kind of mix that shapes how you each show up for one another, whether that's balancing each other out or reinforcing the same instincts.`
      : "";

  const handleHexSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidHex(hexInput)) {
      setHexError("Enter a valid 6-digit hex code, like 6B9080.");
      return;
    }
    setHexError("");
    setFriendHex(normalizeHex(hexInput));
  };

  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 md:py-16 w-full">
        <h1 className="text-3xl sm:text-5xl font-serif mb-4 leading-tight text-center">Color Synergy</h1>

        {!senderHex && (
          <>
            <p className="text-muted-foreground text-base sm:text-lg text-center max-w-xl mx-auto mb-10">
              Color Synergy compares two people's dominant colors and reveals how they blend —
              a shared relationship profile grounded in color psychology. Take the personality
              quiz to get your own shareable link, or pick a color below to generate one now.
            </p>
            <div className="flex flex-col items-center gap-8">
              <Link href="/quiz/personality" className="px-6 py-3 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-medium text-sm">
                Take the Personality Quiz
              </Link>
              <div className="w-full">
                <p className="text-center text-xs uppercase tracking-wide text-muted-foreground font-medium mb-4">
                  Or pick your color to generate a link
                </p>
                <ColorPickerGrid onPick={setOwnHex} />
              </div>
              {ownHex && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="w-full max-w-md rounded-2xl border border-border bg-card p-5 text-center"
                >
                  <span className="inline-block w-10 h-10 rounded-full ring-2 ring-border mb-3" style={{ backgroundColor: ownHex }} />
                  <ShareButtons
                    label="Your Color Synergy link"
                    text="Compare your dominant color to mine on Mychromamind's Color Synergy:"
                    url={buildSynergyShareUrl(ownHex)}
                    
                  />
                </motion.div>
              )}
            </div>
          </>
        )}

        {senderHex && !friendHex && (
          <>
            <p className="text-muted-foreground text-base sm:text-lg text-center max-w-xl mx-auto mb-8">
              A friend shared their dominant color with you. Pick yours below (or enter a hex
              code) to reveal your Compatibility Matrix.
            </p>
            <div className="flex justify-center mb-8">
              <span className="w-16 h-16 rounded-full ring-4 ring-background shadow-lg" style={{ backgroundColor: senderHex }} />
            </div>
            <ColorPickerGrid onPick={setFriendHex} />
            <form onSubmit={handleHexSubmit} className="max-w-xs mx-auto mt-6 flex gap-2">
              <input
                type="text"
                value={hexInput}
                onChange={(e) => setHexInput(e.target.value)}
                placeholder="or enter hex, e.g. 6B9080"
                className="flex-1 px-3 py-2 rounded-lg border border-border bg-card text-sm font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <button type="submit" className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors text-sm font-medium">
                Go
              </button>
            </form>
            {hexError && <p className="text-center text-xs text-destructive mt-2">{hexError}</p>}
          </>
        )}

        {hasMatrix && senderColor && friendColor && synergy && blendHex && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center justify-center gap-4 sm:gap-8 mb-8">
              <div className="text-center">
                <span className="block w-16 h-16 sm:w-20 sm:h-20 rounded-full ring-2 ring-border mb-2 mx-auto" style={{ backgroundColor: senderColor.hex }} />
                <p className="text-xs sm:text-sm font-medium">{senderColor.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{AURA_READS[classifyAuraFamily(senderColor.hex)].title}</p>
              </div>
              <span className="text-2xl text-muted-foreground">×</span>
              <div className="text-center">
                <span className="block w-20 h-20 sm:w-28 sm:h-28 rounded-full ring-4 ring-primary/30 shadow-xl mb-2 mx-auto" style={{ backgroundColor: blendHex }} />
                <p className="text-xs sm:text-sm font-medium">Your Blend</p>
              </div>
              <span className="text-2xl text-muted-foreground">×</span>
              <div className="text-center">
                <span className="block w-16 h-16 sm:w-20 sm:h-20 rounded-full ring-2 ring-border mb-2 mx-auto" style={{ backgroundColor: friendColor.hex }} />
                <p className="text-xs sm:text-sm font-medium">{friendColor.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{AURA_READS[classifyAuraFamily(friendColor.hex)].title}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-serif">Compatibility Matrix</h2>
                <span className="text-2xl font-semibold text-primary">{synergy.score}%</span>
              </div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-4">{synergy.label}</p>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{relationshipText}</p>
            </div>

            <AdSlot slotId="2431849490" content={[relationshipText]} className="mb-8" />

            <div className="text-center mb-8">
              <ShareButtons
                label="Share this result"
                text={`My Color Synergy with a friend scored ${synergy.score}% (${synergy.label}) on Mychromamind:`}
                
              />
            </div>

            <div className="text-center border-t border-border/60 pt-8">
              <p className="text-sm text-muted-foreground mb-4">Want to compare with someone else?</p>
              <ShareButtons
                label="Share your own Color Synergy link"
                text="Compare your dominant color to mine on Mychromamind's Color Synergy:"
                url={buildSynergyShareUrl(friendColor.hex)}
                
              />
            </div>

            <p className="text-center text-xs text-muted-foreground max-w-md mx-auto mt-8">
              For fun and reflection only — not psychological or relationship advice.
            </p>
          </motion.div>
        )}
      </div>
    </Shell>
  );
}
